import React from 'react';
import { Card, Callout } from '../../ui';
import DocumentForm from './DocumentForm';
import { Document } from './ShippingForm';
import { UnitSystem } from '../UnitSystem';

interface DocumentListProps {
  documents: Document[];
  unitSystem: UnitSystem;
  onAddDocument: () => void;
  onRemoveDocument: (id: string) => void;
  onUpdateDocument: (id: string, field: string, value: any) => void;
}

const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  unitSystem,
  onAddDocument,
  onRemoveDocument,
  onUpdateDocument,
}) => {
  const weightUnit = unitSystem === 'metric' ? 'kg' : 'lb';

  const totalWeight = documents.reduce((total, doc) => {
    return total + doc.weight * doc.quantity;
  }, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
      {documents.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {documents.map((document, index) => (
            <Card
              key={document.id}
              pad
              style={{ border: '1px solid var(--line)', transition: 'border-color 0.2s ease' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 16, marginRight: 8 }}>📄</span>
                <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-2)' }}>
                  Document {index + 1}
                </span>
                <div style={{ flex: 1 }} />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 12,
                    background: 'var(--info-bg)',
                    color: 'var(--info)',
                    border: '1px solid var(--info)',
                  }}
                >
                  {document.quantity} × {document.weight}{weightUnit}
                </span>
              </div>
              <DocumentForm
                document={document}
                index={index}
                unitSystem={unitSystem}
                onUpdateDocument={onUpdateDocument}
                onRemoveDocument={onRemoveDocument}
                canRemove={documents.length > 1}
              />
            </Card>
          ))}
        </div>
      ) : (
        <Callout tone="info">
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Add your first document to get started</div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              Include details like size, weight, and quantity for accurate shipping quotes.
            </div>
          </div>
        </Callout>
      )}

      {/* Add Document Button */}
      <button
        type="button"
        onClick={onAddDocument}
        style={{
          padding: '14px 20px',
          border: '2px dashed var(--line)',
          borderRadius: 8,
          background: 'transparent',
          cursor: 'pointer',
          color: 'var(--ink-3)',
          fontSize: 14,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          width: '100%',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--brand-blue)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--brand-blue)';
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(43,85,255,0.04)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--line)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-3)';
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        }}
      >
        <span style={{ fontSize: 18 }}>+</span>
        {documents.length === 0 ? 'Add Your First Document' : 'Add Another Document'}
      </button>

      {/* Weight Summary */}
      {documents.length > 0 && totalWeight > 0 && (
        <Card
          pad
          style={{
            border: '2px solid var(--ok)',
            background: 'var(--ok-bg)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ marginRight: 8, fontSize: 16 }}>✓</span>
            <span style={{ fontWeight: 600, color: 'var(--ok)' }}>Weight Summary</span>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
            {documents.map((doc, index) => (
              <div
                key={doc.id}
                style={{
                  minWidth: 120,
                  padding: '10px 14px',
                  border: '1px solid rgba(15,163,114,0.3)',
                  borderRadius: 8,
                  background: 'rgba(15,163,114,0.06)',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 500, marginBottom: 4 }}>
                  Document {index + 1}
                </div>
                <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--ok)' }}>
                  {(doc.weight * doc.quantity).toFixed(2)} {weightUnit}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {doc.quantity} × {doc.weight}{weightUnit}
                </div>
              </div>
            ))}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid rgba(15,163,114,0.2)', margin: '8px 0 12px' }} />

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 500, marginBottom: 4 }}>
              Total Document Weight
            </div>
            <div style={{ fontWeight: 700, fontSize: 28, color: 'var(--ok)' }}>
              {totalWeight.toFixed(2)} {weightUnit}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ok)', fontWeight: 500, marginTop: 4 }}>
              Ready for shipping calculation
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default DocumentList;
